import React, { ComponentProps, useMemo } from 'react';
import { Extends, getClickableIconColorStyles } from '@hse-design/core';
import clsx from 'clsx';

import { Icon, IconProps } from './Icon';

export type ClickableIconDefaultComponent = 'button';

export interface ClickableIconSelfProps<
  ComponentType extends React.ElementType = ClickableIconDefaultComponent
> {
  /**
   * Компонент
   */
  component?: ComponentType;
  /**
   * Отключенное состояние
   */
  disabled?: boolean;
  /**
   * Состояние ховера
   */
  hovered?: boolean;
  /**
   * Состояние фокуса
   */
  focused?: boolean;
  /**
   * Дополнительные пропы иконки
   */
  iconProps?: IconProps;
  /**
   * Основной цвет иконки
   */
  color?: string;
  /**
   * Реф на корневой элемент
   */
  innerRef?: ComponentProps<ComponentType>['ref'];
}

export type ClickableIconProps<
  ComponentType extends React.ElementType = ClickableIconDefaultComponent
> = Extends<
  ClickableIconSelfProps<ComponentType>,
  React.ComponentProps<ComponentType>
>;

export function ClickableIcon<
  ComponentType extends React.ElementType = ClickableIconDefaultComponent
>({
  component,
  className,
  disabled,
  hovered,
  focused,
  iconProps,
  color = '',
  innerRef,
  ...props
}: ClickableIconProps<ComponentType>) {
  const iconStyles = useMemo(() => {
    return getClickableIconColorStyles(color);
  }, [color]);
  const Component = (component || 'button') as React.ElementType;
  const buttonProps = useMemo(
    () => (component === 'button' ? { type: 'button', disabled } : {}),
    [component]
  );
  if (!Icon) return null;

  return (
    <Component
      ref={innerRef}
      className={clsx('hse-ClickableIcon', className, {
        'hse-ClickableIcon_disabled': disabled,
        'hse-ClickableIcon_hovered': hovered,
        'hse-ClickableIcon_focused': focused,
        'hse-ClickableIcon_controlledFocus': typeof focused !== 'undefined'
      })}
      {...buttonProps}
      {...props}
    >
      <Icon
        {...iconProps}
        className={clsx('hse-ClickableIcon__icon', iconProps?.className, {
          'hse-ClickableIcon__icon_disabled': disabled,
          'hse-ClickableIcon__icon_hovered': hovered,
          'hse-ClickableIcon__icon_focused': focused
        })}
        style={{ ...iconProps?.style, ...iconStyles }}
      />
    </Component>
  );
}

ClickableIcon.Size = Icon.Size;
